import type { EventData } from '@/api/types/events';

export const ATTRIBUTION_STORAGE_KEY = 'co_attribution_v1';
export const ATTRIBUTION_COOKIE_KEY = 'co_attribution_v1';

const STANDARD_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const CLICK_ID_KEYS = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'twclid', 'li_fat_id'] as const;
const MAX_PARAM_KEY_LENGTH = 64;
const MAX_PARAM_VALUE_LENGTH = 180;
const MAX_PARAM_COUNT = 30;
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

type StandardUtmKey = (typeof STANDARD_UTM_KEYS)[number];
type ClickIdKey = (typeof CLICK_ID_KEYS)[number];

export type AttributionParams = Partial<Record<`utm_${string}`, string>>;
export type ClickIdParams = Partial<Record<ClickIdKey, string>>;

export interface AttributionTouch {
  captured_at: string;
  page_path?: string;
  referrer?: string;
  utm_params: AttributionParams;
  click_ids?: ClickIdParams;
}

export interface StoredAttribution {
  version: 1;
  first_touch?: AttributionTouch;
  last_touch?: AttributionTouch;
}

interface MergeOptions {
  preferFirstTouch?: boolean;
}

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const sanitizeText = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_PARAM_VALUE_LENGTH);
};

const sanitizeParams = <T extends Record<string, string | undefined>>(input: T): Partial<T> => {
  const output: Partial<T> = {};
  let count = 0;

  for (const [rawKey, rawValue] of Object.entries(input)) {
    if (count >= MAX_PARAM_COUNT) break;
    const key = rawKey.slice(0, MAX_PARAM_KEY_LENGTH) as keyof T;
    const value = sanitizeText(rawValue);
    if (!value) continue;
    output[key] = value as T[keyof T];
    count += 1;
  }

  return output;
};

const hasValues = (value?: Record<string, string | undefined>): boolean =>
  Boolean(value && Object.values(value).some(Boolean));

const readCookieValue = (name: string): string | null => {
  if (!isBrowser()) return null;
  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`));

  if (!cookie) return null;
  return cookie.slice(name.length + 1);
};

const writeCookieValue = (name: string, value: string) => {
  if (!isBrowser()) return;
  const isSecure = window.location.protocol === 'https:';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`;
};

export const parseStoredAttribution = (rawValue?: string | null): StoredAttribution | null => {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue) as StoredAttribution;
    if (!parsed || parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const parseStoredAttributionCookie = (rawCookieValue?: string | null): StoredAttribution | null => {
  if (!rawCookieValue) return null;
  try {
    const decoded = decodeURIComponent(rawCookieValue);
    return parseStoredAttribution(decoded);
  } catch {
    return parseStoredAttribution(rawCookieValue);
  }
};

export const getStoredAttribution = (): StoredAttribution | null => {
  if (!isBrowser()) return null;

  const fromLocalStorage = parseStoredAttribution(window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY));
  if (fromLocalStorage) return fromLocalStorage;

  return parseStoredAttributionCookie(readCookieValue(ATTRIBUTION_COOKIE_KEY));
};

const saveStoredAttribution = (attribution: StoredAttribution): void => {
  if (!isBrowser()) return;
  const serialized = JSON.stringify(attribution);
  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, serialized);
  writeCookieValue(ATTRIBUTION_COOKIE_KEY, serialized);
};

const collectUtmParams = (searchParams: URLSearchParams): AttributionParams => {
  const params: Record<string, string | undefined> = {};

  searchParams.forEach((value, key) => {
    if (!key.toLowerCase().startsWith('utm_')) return;
    params[key.toLowerCase()] = value;
  });

  return sanitizeParams(params) as AttributionParams;
};

const collectClickIds = (searchParams: URLSearchParams): ClickIdParams => {
  const params: Record<string, string | undefined> = {};

  for (const key of CLICK_ID_KEYS) {
    params[key] = searchParams.get(key) ?? undefined;
  }

  return sanitizeParams(params) as ClickIdParams;
};

const buildTouch = ({
  searchParams,
  pagePath,
  referrer,
}: {
  searchParams: URLSearchParams;
  pagePath?: string;
  referrer?: string;
}): AttributionTouch | null => {
  const utmParams = collectUtmParams(searchParams);
  const clickIds = collectClickIds(searchParams);

  if (!hasValues(utmParams) && !hasValues(clickIds)) return null;

  return {
    captured_at: new Date().toISOString(),
    page_path: sanitizeText(pagePath),
    referrer: sanitizeText(referrer),
    utm_params: utmParams,
    click_ids: hasValues(clickIds) ? clickIds : undefined,
  };
};

export const captureAttributionFromBrowser = (): StoredAttribution | null => {
  if (!isBrowser()) return null;

  const searchParams = new URLSearchParams(window.location.search);
  const touch = buildTouch({
    searchParams,
    pagePath: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || undefined,
  });

  const existing = getStoredAttribution();
  if (!touch) return existing;

  const updated: StoredAttribution = {
    version: 1,
    first_touch: existing?.first_touch ?? touch,
    last_touch: touch,
  };

  saveStoredAttribution(updated);
  return updated;
};

export const appendCurrentAttributionToUrl = (targetUrl: string): string => {
  if (!isBrowser()) return targetUrl;

  const baseUrl = new URL(targetUrl, window.location.origin);
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.forEach((value, key) => {
    const lowered = key.toLowerCase();
    if (!lowered.startsWith('utm_') && !CLICK_ID_KEYS.includes(lowered as ClickIdKey)) return;
    if (!baseUrl.searchParams.has(lowered)) {
      baseUrl.searchParams.set(lowered, value);
    }
  });

  return baseUrl.toString();
};

const getPreferredTouch = (attribution: StoredAttribution | null, options?: MergeOptions): AttributionTouch | undefined => {
  if (!attribution) return undefined;
  if (options?.preferFirstTouch) {
    return attribution.first_touch ?? attribution.last_touch;
  }
  return attribution.last_touch ?? attribution.first_touch;
};

const getStandardUtmValue = (
  key: StandardUtmKey,
  preferredTouch?: AttributionTouch,
  fallbackTouch?: AttributionTouch,
): string | undefined => {
  return preferredTouch?.utm_params[key] ?? fallbackTouch?.utm_params[key];
};

export const mergeEventWithStoredAttribution = (
  eventData: EventData,
  options?: MergeOptions,
): EventData => {
  const attribution = getStoredAttribution();
  if (!attribution) return eventData;

  const preferredTouch = getPreferredTouch(attribution, options);
  const fallbackTouch = attribution.first_touch ?? attribution.last_touch;
  const mergedDetails = { ...(eventData.event_details ?? {}) };

  if (attribution.first_touch) {
    mergedDetails.attribution_first_touch = attribution.first_touch;
  }
  if (attribution.last_touch) {
    mergedDetails.attribution_last_touch = attribution.last_touch;
  }

  if (preferredTouch?.utm_params && hasValues(preferredTouch.utm_params)) {
    mergedDetails.utm_params = {
      ...(mergedDetails.utm_params as Record<string, string> | undefined),
      ...preferredTouch.utm_params,
    };
  }

  if (preferredTouch?.click_ids && hasValues(preferredTouch.click_ids)) {
    mergedDetails.click_ids = {
      ...(mergedDetails.click_ids as Record<string, string> | undefined),
      ...preferredTouch.click_ids,
    };
  }

  return {
    ...eventData,
    utm_source: eventData.utm_source ?? getStandardUtmValue('utm_source', preferredTouch, fallbackTouch),
    utm_medium: eventData.utm_medium ?? getStandardUtmValue('utm_medium', preferredTouch, fallbackTouch),
    utm_campaign: eventData.utm_campaign ?? getStandardUtmValue('utm_campaign', preferredTouch, fallbackTouch),
    utm_content: eventData.utm_content ?? getStandardUtmValue('utm_content', preferredTouch, fallbackTouch),
    utm_term: eventData.utm_term ?? getStandardUtmValue('utm_term', preferredTouch, fallbackTouch),
    event_details: mergedDetails,
  };
};

export const getAttributionSourceAndCampaign = (
  attribution: StoredAttribution | null,
): { sourceName?: string; feedName?: string } => {
  const firstTouch = attribution?.first_touch;
  if (!firstTouch) return {};

  const sourceName = sanitizeText(firstTouch.utm_params.utm_source);
  const campaign = sanitizeText(firstTouch.utm_params.utm_campaign);
  const medium = sanitizeText(firstTouch.utm_params.utm_medium);

  if (!sourceName && !campaign && !medium) return {};

  return {
    sourceName: sourceName || medium,
    feedName: campaign || medium,
  };
};
